import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiX } from 'react-icons/fi';
import apiClient from '../utils/apiClient';
import { useToast } from '../context/ToastContext';
import { auth } from '../utils/firebase';
import '../styles/Addresses.css';

const Addresses = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const [formData, setFormData] = useState({
    label: '', // home, office, or custom
    customLabel: '', // for custom label input
    fullName: '',
    phone: '',
    houseNo: '',
    street: '', // optional
    sector: '', // optional
    landmark: '', // optional
    city: '',
    state: '',
    pincode: '',
    isDefault: false,
  });

  const addressLabels = ['Home', 'Office', 'Custom'];

  // Fetch addresses on component mount
  useEffect(() => {
    // Wait for Firebase auth to be ready, then fetch addresses
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Get fresh token and add to headers
        try {
          const idToken = await user.getIdToken();
          // Create a config with the token
          const config = {
            headers: {
              Authorization: `Bearer ${idToken}`
            }
          };
          setLoading(true);
          const response = await apiClient.get('/addresses', config);
          setAddresses(response.data || []);
        } catch (error) {
          console.error('Error fetching addresses:', error);
          toast.error('Failed to load addresses');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const getAuthConfig = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    const idToken = await user.getIdToken();
    return {
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    };
  };

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const config = await getAuthConfig();
      const response = await apiClient.get('/addresses', config);
      setAddresses(response.data || []);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      toast.error('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = () => {
    setFormData({
      label: '',
      customLabel: '',
      fullName: '',
      phone: '',
      houseNo: '',
      street: '',
      sector: '',
      landmark: '',
      city: '',
      state: '',
      pincode: '',
      isDefault: false,
    });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEditAddress = (address) => {
    // Parse the address field back into components if it's a string
    const addressData = { ...address };
    if (typeof address.address === 'string') {
      // For existing addresses, keep the address as is in houseNo
      addressData.houseNo = address.address;
      addressData.street = '';
      addressData.sector = '';
      addressData.landmark = '';
    }
    setFormData(addressData);
    setEditingId(address.id);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      label: '',
      customLabel: '',
      fullName: '',
      phone: '',
      houseNo: '',
      street: '',
      sector: '',
      landmark: '',
      city: '',
      state: '',
      pincode: '',
      isDefault: false,
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Use custom label if 'Custom' is selected, otherwise use the selected label
    const finalLabel = formData.label === 'Custom' ? formData.customLabel : formData.label;

    if (!finalLabel || !formData.fullName || !formData.phone || !formData.houseNo || !formData.city || !formData.state || !formData.pincode) {
      toast.error('Please fill all required fields');
      return;
    }

    // If Custom is selected, validate that customLabel is provided
    if (formData.label === 'Custom' && !formData.customLabel.trim()) {
      toast.error('Please enter a custom label name');
      return;
    }

    // Validate phone
    if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate pincode
    if (!/^\d{6}$/.test(formData.pincode)) {
      toast.error('Please enter a valid 6-digit pincode');
      return;
    }

    try {
      setSubmitting(true);

      const config = await getAuthConfig();

      // Combine address components into single address field for backend
      const addressParts = [
        formData.houseNo,
        formData.street,
        formData.sector,
        formData.landmark
      ].filter(part => part && part.trim()).join(', ');

      // Prepare data with final label and combined address
      const submitData = {
        label: finalLabel,
        fullName: formData.fullName,
        phone: formData.phone,
        address: addressParts,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        isDefault: formData.isDefault
      };

      if (editingId) {
        // Update existing address
        await apiClient.put(`/addresses/${editingId}`, submitData, config);
        toast.success('Address updated successfully');
      } else {
        // Create new address
        await apiClient.post('/addresses', submitData, config);
        toast.success('Address added successfully');
      }

      await fetchAddresses();
      handleCloseForm();
    } catch (error) {
      console.error('Error saving address:', error);
      toast.error(error.response?.data?.message || 'Failed to save address');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const idToken = await user.getIdToken();

      await apiClient.delete(`/addresses/${addressId}`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      toast.success('Address deleted successfully');
      await fetchAddresses();
    } catch (error) {
      console.error('Error deleting address:', error);
      toast.error('Failed to delete address');
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const idToken = await user.getIdToken();

      await apiClient.put(`/addresses/${addressId}/set-default`, {}, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      toast.success('Default address updated');
      await fetchAddresses();
    } catch (error) {
      console.error('Error setting default address:', error);
      toast.error('Failed to set default address');
    }
  };

  if (loading && addresses.length === 0) {
    return <div className="loading">Loading addresses...</div>;
  }

  return (
    <div className="addresses-container">
      <div className="addresses-header">
        <h2>Saved Addresses</h2>
        <button className="add-address-btn" onClick={handleAddAddress}>
          <FiPlus /> Add New Address
        </button>
      </div>

      {showForm && (
        <div className="address-form-modal">
          <div className="address-form-container">
            <div className="form-header">
              <h3>{editingId ? 'Edit Address' : 'Add New Address'}</h3>
              <button className="close-btn" onClick={handleCloseForm}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="address-form">
              <div className="form-group">
                <label>Address Label</label>
                <select
                  name="label"
                  value={formData.label}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select label</option>
                  {addressLabels.map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.label === 'Custom' && (
                <div className="form-group">
                  <label>Custom Label Name</label>
                  <input
                    type="text"
                    name="customLabel"
                    value={formData.customLabel}
                    onChange={handleInputChange}
                    placeholder="e.g., Work, Parent's House, etc."
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="10-digit phone number"
                  required
                />
              </div>


              <div className="form-group">
                <label>House No. / Building Name *</label>
                <input
                  type="text"
                  name="houseNo"
                  value={formData.houseNo}
                  onChange={handleInputChange}
                  placeholder="Enter house/flat number or building name"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Street</label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    placeholder="Street name (optional)"
                  />
                </div>

                <div className="form-group">
                  <label>Sector</label>
                  <input
                    type="text"
                    name="sector"
                    value={formData.sector}
                    onChange={handleInputChange}
                    placeholder="Sector (optional)"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Landmark</label>
                <input
                  type="text"
                  name="landmark"
                  value={formData.landmark}
                  onChange={handleInputChange}
                  placeholder="Nearby landmark (optional)"
                />
              </div>


              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="State"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    placeholder="6-digit pincode"
                    required
                  />
                </div>
              </div>

              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  id="isDefault"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleInputChange}
                />
                <label htmlFor="isDefault">Set as default address</label>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="save-btn"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Address'}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCloseForm}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="addresses-list">
        {addresses.length === 0 ? (
          <div className="empty-state">
            <p>No addresses saved yet</p>
          </div>
        ) : (
          addresses.map((address) => (
            <div key={address.id} className={`address-card ${address.isDefault ? 'default' : ''}`}>
              {address.isDefault && <div className="default-badge">Default</div>}

              <div className="address-label">{address.label}</div>

              <div className="address-details">
                <p className="name">
                  <strong>{address.fullName}</strong>
                </p>
                <p className="phone">{address.phone}</p>
                <p className="address">{address.address}</p>
                <p className="location">
                  {address.city}, {address.state} - {address.pincode}
                </p>
              </div>

              <div className="address-actions">
                <button
                  className="edit-btn"
                  onClick={() => handleEditAddress(address)}
                  title="Edit address"
                >
                  <FiEdit2 /> Edit
                </button>

                {!address.isDefault && (
                  <button
                    className="default-btn"
                    onClick={() => handleSetDefault(address.id)}
                    title="Set as default"
                  >
                    Set Default
                  </button>
                )}

                <button
                  className="delete-btn"
                  onClick={() => handleDeleteAddress(address.id)}
                  title="Delete address"
                >
                  <FiTrash2 /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Addresses;
